import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAllUserMeetings } from "../../features/meetings/meetingsSlice";
import NotFoundMessage from "../../components/common/Modal/NotFoundMessage";
import LoadingComponent from "../../components/common/LoadingComponent";
import { Icons } from "../../constants/icon-library";
import { fetchIntegrationSettingsByUserId } from "../../features/integration-setting/integrationSettingSlice";
import { getIcon } from "../../constants/constants";
import { AVATAR_COLORS, getInitials, getStatusBadge } from "./Meetings.utils";
import { toast } from "react-toastify";
import { refreshIntegrationSetting } from "../../features/integration-setting/integrationSettingSlice";

const ParticipantCell = ({ participants = [] }) => {
  const [show, setShow] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  useEffect(() => {
    if (show && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const isBottom = window.innerHeight - rect.bottom < 150;
      setTooltipPos({
        top: isBottom
          ? rect.top + window.scrollY - 10
          : rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX + 4,
      });
    }
  }, [show]);

  const shown = participants.slice(0, 3);
  const extra = participants.length - shown.length;

  if (participants.length === 0) {
    return <span className="text-body-quaternary fs--2">—</span>;
  }

  return (
    <div
      ref={triggerRef}
      className="d-inline-flex align-items-center"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <div className="d-flex align-items-center">
        {shown.map((p, i) => (
          <div
            key={p.uuid}
            title={p.name}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: "2px solid var(--phoenix-card-bg, #fff)",
              background: AVATAR_COLORS[i % AVATAR_COLORS.length],
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 8,
              fontWeight: 700,
              marginLeft: i === 0 ? 0 : -6,
              zIndex: shown.length - i,
              position: "relative",
              flexShrink: 0,
            }}
          >
            {getInitials(p.name)}
          </div>
        ))}
        {extra > 0 && (
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: "2px solid var(--phoenix-card-bg, #fff)",
              background: "var(--phoenix-emphasis-bg)",
              color: "var(--phoenix-secondary-color)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 8,
              fontWeight: 700,
              marginLeft: -6,
              flexShrink: 0,
            }}
          >
            +{extra}
          </div>
        )}
      </div>

      {/* Portal tooltip */}
      {show &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "absolute",
              top: tooltipPos.top,
              left: tooltipPos.left,
              background: "var(--phoenix-emphasis-bg)",
              border: "1px solid var(--phoenix-border-color)",
              borderRadius: 10,
              padding: "6px 10px",
              minWidth: 160,
              zIndex: 9999,
              boxShadow: "0 8px 24px rgba(0,0,0,.18)",
              pointerEvents: "none",
            }}
          >
            {participants.map((p, i) => (
              <div
                key={p.uuid}
                className="d-flex align-items-center gap-2 py-1"
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 8,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(p.name)}
                </div>
                <span className="fs--2 text-body-secondary text-nowrap">
                  {p.name}
                </span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

const TaskProgress = ({ meeting = {} }) => {
  const total = meeting?.tasks?.total ?? 0;
  const completed = meeting?.tasks?.completed ?? 0;

  if (total === 0)
    return (
      <span className="text-body-quaternary fs--2 align-middle">No tasks</span>
    );

  const pct = Math.round((completed / total) * 100);

  return (
    <div className="d-flex align-items-center gap-2">
      <div className="progress" style={{ height: 4, width: 64 }}>
        <div className="progress-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="fs--2">
        {completed}/{total}
      </span>
    </div>
  );
};

const MeetingsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { integrationSettings = [] } = useSelector(
    (state) => state.integrationSetting || {},
  );
  const { meetings, loading } = useSelector((state) => state.meetings);
  const { user } = useSelector((state) => state.user);
  const [refreshLoading, setRefreshLoading] = useState(false);

  useEffect(() => {
    if (user?.uuid) {
      dispatch(fetchIntegrationSettingsByUserId(user.uuid));
    }
  }, [user?.uuid, dispatch]);

  useEffect(() => {
    dispatch(getAllUserMeetings());
  }, [dispatch]);

  const handleRefresh = async () => {
    if (!integrationSettings.length) {
      toast.warning("No integration connected to refresh data");
      return;
    }

    try {
      setRefreshLoading(true);

      const result = await dispatch(refreshIntegrationSetting());

      if (result?.payload?.success) {
        toast.success(result.payload.message);

        await dispatch(getAllUserMeetings());
      } else {
        toast.error(result?.payload?.message || "Failed to refresh data");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to refresh data");
    } finally {
      setRefreshLoading(false);
    }
  };

  const showNotFound = !loading && meetings.length === 0;

  return (
    <div className="px-3 px-md-4 py-10">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="mb-0 fw-semibold">Meetings</h3>

        <div className="d-flex align-items-center">
          {integrationSettings.length > 0 && (
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              style={{
                padding: "8px 16px",
                fontSize: "12px",
                marginRight: "20px",
                borderRadius: "8px",
              }}
              disabled={refreshLoading}
              onClick={handleRefresh}
            >
              {refreshLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i
                    className={`wb ${Icons("load")}`}
                    style={{ fontSize: "16px" }}
                  />
                  Refresh Integrations Data
                </>
              )}
            </button>
          )}

          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            style={{
              padding: "10px 22px",
              fontSize: "16px",
              marginRight: "20px",
              borderRadius: "8px",
            }}
            onClick={() => navigate("create")}
          >
            <i className={`wb ${Icons("add")}`} style={{ fontSize: "16px" }} />
            Meeting
          </button>
        </div>
      </div>
      {loading && <LoadingComponent loading />}

      {showNotFound && (
        <NotFoundMessage
          message="No meetings found."
          onAddNew={() => navigate("create")}
          buttonMessage="Add Meeting"
        />
      )}

      {!loading && !showNotFound && (
        <div className="d-flex flex-column gap-3">
          {meetings.map((meeting) => (
            <div
              key={meeting.uuid}
              className="card border shadow-none"
              style={{ borderRadius: 10, cursor: "pointer" }}
              onClick={() => navigate(meeting.uuid)}
            >
              <div className="card-body py-3 px-3">
                <div className="d-flex align-items-center justify-content-between gap-3">
                  {/* LEFT */}
                  <div className="d-flex align-items-center gap-3 flex-grow-1">
                    <img
                      src={
                        meeting.settingId?.integrationId?.name
                          ? getIcon(meeting.settingId.integrationId.name)
                          : "/logo192.png"
                      }
                      alt="logo"
                      style={{ width: 28, height: 28 }}
                    />

                    <div className="fw-semibold text-truncate">
                      {meeting.title || "Untitled Meeting"}
                    </div>
                  </div>

                  {/* RIGHT */}
                  <div className="d-flex align-items-center gap-4 flex-shrink-0">
                    <div
                      className="d-flex align-items-center justify-content-start"
                      style={{ minWidth: 70 }}
                    >
                      <ParticipantCell
                        participants={meeting.participants ?? []}
                      />
                    </div>
                    <div
                      className="d-none d-sm-block"
                      style={{
                        width: 1,
                        height: 16,
                        background: "var(--phoenix-border-color)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      className={`${getStatusBadge(meeting.status)} fs--2 d-flex align-items-center justify-content-center`}
                      style={{ minWidth: 90 }}
                    >
                      {meeting.status}
                    </span>
                    <div
                      className="d-none d-sm-block"
                      style={{
                        width: 1,
                        height: 16,
                        background: "var(--phoenix-border-color)",
                        flexShrink: 0,
                      }}
                    />
                    <div
                      className="d-flex align-items-center"
                      style={{ minWidth: 100 }}
                    >
                      <TaskProgress meeting={meeting} />
                    </div>
                    <div
                      className="d-none d-sm-block"
                      style={{
                        width: 1,
                        height: 16,
                        background: "var(--phoenix-border-color)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      className="fs--2 text-body-secondary d-flex align-items-center justify-content-center"
                      style={{ minWidth: 110 }}
                    >
                      {new Date(
                        meeting.date || meeting.createdAt,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <button
                    className="btn btn-primary btn-sm flex-shrink-0 d-flex align-items-center justify-content-center"
                    style={{ padding: "3px 8px", minWidth: 28 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(meeting.uuid);
                    }}
                  >
                    <i
                      className={`wb ${Icons("view")}`}
                      style={{ fontSize: 11 }}
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MeetingsList;
